import React, { FC } from 'react'
import { useIntl } from 'react-intl'
import Chart from 'react-apexcharts';
import { ID } from '@/_metronic/helpers'
import { useState, useEffect } from 'react'
import { add, startOfMonth, parseISO, differenceInHours, differenceInDays } from 'date-fns'
import { EvolutionData } from '../core/_models'
import { getEvolution } from '../core/_requests'
import { RestockData } from '../../product/core/_models'
import { getRestocks } from '../../product/core/_requests'
import { getCSSVariableValue } from '@/_metronic/assets/ts/_utils'

type IDatePad = 'day' | 'hour' | 'month' | 'year';
type Props = {
  userId?: ID
  from: Date
  to: Date
  timezone: string;
  restock?: boolean
  height: number
}

const fill = (from: Date, to: Date, data: EvolutionData[], unit: IDatePad): EvolutionData[] => {
  const ticksMap = new Map<number, EvolutionData>();
  const unitKey = unit + 's';
  let current;

  switch (unit) {
    case 'day':
    case 'hour':
      current = from;
      break;
    default:
      current = startOfMonth(from);
  }

  while (current.getTime() < to.getTime()) {
    ticksMap.set(current.getTime(), {
      date: current,
      value: { order: 0, trx: 0, total: 0 }
    });
    current = add(current, { [unitKey]: 1 });
  }

  // Remplir avec les vraies données
  data.forEach(entry => {
    // Parser la date ISO (avec timezone) correctement
    const periodDate = typeof entry.date === 'string'
      ? parseISO(entry.date)
      : new Date(entry.date);

    const timestamp = periodDate.getTime();

    if (ticksMap.has(timestamp)) {
      ticksMap.set(timestamp, {
        date: periodDate,
        value: entry.value
      });
    }
  });

  return Array.from(ticksMap.values());
};


const computePad = (from: Date, to: Date): string => {
  const hoursDiff = differenceInHours(to, from);
  const daysDiff = differenceInDays(to, from);

  if (hoursDiff <= 48) return 'hour';
  if (daysDiff <= 60) return 'day';
  return 'month';
}

const EarningsEvolutionChart: FC<Props> = ({ userId, from, to, restock, height, timezone }) => {
  const intl = useIntl()
  const [data, setData] = useState<Array<EvolutionData> | undefined>()
  const [rData, setRdata] = useState<Array<RestockData>>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const pad = computePad(from, to) as IDatePad

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      try {
        setData(await getEvolution({ from, to, userId, timezone, pad }) || [])
        if (restock === true) {
          setRdata(await getRestocks({ from, to, timezone, pad }) || [])
        }
      } catch (e: unknown) {
        console.log(e)
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [userId, from, to, restock])

  if (isLoading || data === undefined || data.length === 0) {
    return (
      <div
        className="align-items-center d-grid text-center"
        style={{ minHeight: `${height}px` }}
      >
        {isLoading ? (
          <span>
            <i className='spinner-border fs-5x'></i>
          </span>
        ) : (
          <span>No data</span>
        )}
      </div>
    )
  }

  const filledData = fill(from, to, data, pad);
  const series = [{
    name: 'Total',
    type: 'area',
    data: filledData.map(d => d.value.total)
  }, {
    name: 'Orders',
    type: 'line',
    data: filledData.map(d => d.value.order)
  }, {
    name: 'Transactions',
    type: 'line',
    data: filledData.map(d => d.value.trx)
  }]

  const options: ApexCharts.ApexOptions = {
    chart: {
      id: 'earnings-evolution',
      type: "line",
      stacked: false,
      toolbar: { show: false },
      zoom: { enabled: false }
    },
    colors: [
      getCSSVariableValue('--bs-info'),
      getCSSVariableValue('--bs-primary'),
      getCSSVariableValue('--bs-warning'),
    ],
    stroke: {
      width: [4, 4, 4]
    },
    annotations: {
      xaxis: rData.map((d: RestockData) => {
        const amount = (d.amount / 1000).toFixed(2).replace(/\.0$/, '') + 'K€';

        return {
          x: new Date(d.date).getTime(),
          borderColor: getCSSVariableValue('--bs-danger'),
          label: {
            orientation: 'horizontal',
            style: {
              color: '#fff',
              background: getCSSVariableValue('--bs-danger')
            },
            text: `[${d.restocks}]${amount}`
          }
        }
      })
    },

    xaxis: {
      type: 'datetime' as const,
      categories: filledData.map(d => d.date.getTime()),
      min: filledData.at(0)?.date?.getTime(),
      max: filledData.at(-1)?.date?.getTime(),
      labels: {
        datetimeUTC: false,
        datetimeFormatter: {
          year: 'yyyy', // Format the year
          month: 'MMM yyyy', // Format the month
          day: 'dd MMM yyyy', // Format the day
          hour: 'HH:mm' // Format the hour
        }
      }
    },
    yaxis: [{
      seriesName: 'Total',
      labels: {
        formatter: function (value) {
          return intl.formatNumber(value || 0, {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
        }
      }
    }],
    dataLabels: { enabled: false },
    markers: { size: 5 },
    tooltip: {
      x: {
        format: pad === 'hour' ? 'dd MMM yyyy - HH:mm' : 'dd MMM yyyy'
      }
    },
  }

  return (
    <Chart options={options} series={series} type="line" height={height} />
  )
}

export default EarningsEvolutionChart
